import { describeMutationError } from "@api/mutationErrors";
import { usePrincipalKey } from "@auth/sessionStore";
import { useUser } from "@context/UserContext";
import { useVoice } from "@context/VoiceSessionContext";
import { EmailAssociation } from "@email/EmailAssociation";
import {
  emailClient,
  isEditableDraft,
  isEmailUnavailable,
} from "@email/emailClient";
import { EmailConversationContext } from "@email/EmailConversationContext";
import {
  emailKeys,
  useEmailDraft,
  useEmailConversation,
  useEmailConversations,
  useEmailMessage,
  useEmailSetup,
} from "@email/emailQueries";
import { markEmailRead, useEmailReadMarkers } from "@email/emailReadMarkers";
import type { EmailSearch } from "@email/emailSearch";
import {
  faMicrophone,
  faMinus,
  faTrash,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  FieldError,
  PaperPanel,
} from "@scribenote/brian-design-system";
import { useOpenEmail } from "@shell/navigation";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { canAccessAdmin } from "@users/userClient";
import { formatDateTime } from "@utils/format-date";
import { useEffect, useState } from "react";

type Selection = { kind: "draft" | "message"; uuid: string };
const statusLabel = (status: string) =>
  ({
    needs_approval: "Needs approval",
    draft_ready: "Draft saved",
    needs_attention: "Needs attention",
    draft: "Needs approval",
    queued: "Queued",
    running: "Working",
    completed: "Completed",
    skipped: "No action needed",
    sent: "Sent",
    received: "Received",
    sending: "Sending",
    uncertain: "Check delivery",
    failed: "Needs attention",
  })[status] ?? status.replace(/_/g, " ");

export const EmailActivityWorkspace = ({
  search: { draft: requestedDraft, message: requestedMessage },
}: {
  search: EmailSearch;
}) => {
  const voice = useVoice();
  const { currentUser } = useUser();
  const openEmail = useOpenEmail();
  const principal = usePrincipalKey();
  const [filterState, setFilter] = useState("Brian’s work");
  const filter = requestedMessage ? "Inbox" : filterState;
  const [selectionState, setSelection] = useState<Selection>();
  const selection: Selection | undefined = requestedDraft
    ? { kind: "draft", uuid: requestedDraft }
    : requestedMessage
      ? { kind: "message", uuid: requestedMessage }
      : selectionState;
  const setup = useEmailSetup(principal);
  const enabled = !!setup.data?.mailbox && !setup.error;
  const conversations = useEmailConversations(
    principal,
    filter === "Inbox",
    enabled,
  );
  const context = useEmailConversation(principal, selection);
  const draftUuid = selection?.kind === "draft" ? selection.uuid : undefined;
  const draftQuery = useEmailDraft(principal, draftUuid);
  const contextDraft = context.data?.drafts.find(
    (item) => item.uuid === draftUuid,
  );
  const draft =
    contextDraft && contextDraft.revision > (draftQuery.data?.revision ?? 0)
      ? contextDraft
      : draftQuery.data;
  const messageUuid =
    selection?.kind === "message" ? selection.uuid : undefined;
  const messageQuery = useEmailMessage(principal, messageUuid);
  const message = messageQuery.data;
  const clearSearch = () => openEmail({}, { replace: true });
  const minimizeDraft = () => {
    setFilter(filter);
    setSelection(undefined);
    clearSearch();
  };
  const cache = useQueryClient();
  useEffect(() => {
    if (draftUuid && draft?.status) {
      void cache.invalidateQueries({
        queryKey: emailKeys.conversation(principal, "draft", draftUuid),
      });
    }
  }, [cache, principal, draftUuid, draft?.status, draft?.providerId]);
  const discard = useMutation({
    mutationFn: (uuid: string) => emailClient.discardDraft(uuid),
    meta: { handled: true },
    onSuccess: async () => {
      minimizeDraft();
      await cache.invalidateQueries({ queryKey: emailKeys.all });
    },
  });
  const error = setup.error ?? conversations.error;
  const readStorageKey = `brian-email-read:${principal}`;
  const seenReplies = useEmailReadMarkers(readStorageKey);
  const latestReadReplyId = context.data?.messages
    .filter((item) => !item.mine)
    .sort(
      (a, b) =>
        (Date.parse(b.createdAt ?? "") || 0) -
        (Date.parse(a.createdAt ?? "") || 0),
    )[0]?.providerId;
  useEffect(() => {
    if (context.data?.conversationKey && latestReadReplyId)
      markEmailRead(
        readStorageKey,
        context.data.conversationKey,
        latestReadReplyId,
      );
  }, [readStorageKey, context.data?.conversationKey, latestReadReplyId]);
  const activity = (conversations.data?.items ?? []).map((item) => ({
    kind: item.kind as "draft" | "message",
    uuid: item.uuid,
    date: item.latest_at,
    title: item.subject || "Untitled conversation",
    label: `${item.message_count} saved email${item.message_count === 1 ? "" : "s"}`,
    status: item.status,
    summary: item.participant,
    preview: item.preview,
    newReply:
      !!item.latest_incoming_id &&
      seenReplies[item.key] !== item.latest_incoming_id,
  }));
  const relatedDrafts = [...(context.data?.drafts ?? [])];
  if (draft) {
    const index = relatedDrafts.findIndex((item) => item.uuid === draft.uuid);
    if (index < 0) {
      if (!draft.providerId) relatedDrafts.push(draft);
    } else if (relatedDrafts[index].revision <= draft.revision)
      relatedDrafts[index] = draft;
  }
  if (isEmailUnavailable(setup.error))
    return (
      <PaperPanel className="p-6">
        Email is not available for this clinic. Ask Brian support to enable it.
      </PaperPanel>
    );

  return (
    <div className="font-body text-ink-body" data-dd-privacy="hidden">
      <div className="grid gap-6">
        <main className="min-w-0">
          {error && (
            <FieldError>
              {describeMutationError(error, "load email")}
            </FieldError>
          )}
          <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-sm text-annotation">Brian / Email</p>
              <h2 className="mt-1 font-display text-3xl text-ink">Email</h2>
              <p className="mt-2 text-sm text-annotation">
                {setup.data?.mailbox ?? "No mailbox connected"}
              </p>
            </div>
          </div>
          {!setup.isLoading &&
            !setup.error &&
            !setup.data?.mailbox &&
            currentUser &&
            (canAccessAdmin(currentUser) ? (
              <PaperPanel className="mb-4 p-5">
                <p className="mb-3">
                  Connect the clinic mailbox to start using your email
                  assistant.
                </p>
                <Link
                  to="/settings"
                  search={{ tab: "email" }}
                  className="text-accent underline underline-offset-2"
                >
                  Open Admin email settings
                </Link>
              </PaperPanel>
            ) : (
              <PaperPanel className="mb-4 p-5">
                The clinic mailbox is not connected yet. Ask a practice manager
                to connect it before using your email assistant.
              </PaperPanel>
            ))}
          <PaperPanel className="mb-5 flex flex-wrap items-center justify-between gap-5 border border-accent/20 bg-accent/5 p-5">
            <div>
              <p className="font-display text-xl text-ink">
                Work on email with Brian.
              </p>
              <p className="mt-1 text-sm text-annotation">
                Ask Brian to draft a new email or make changes to an existing
                one.
              </p>
            </div>
            <button
              type="button"
              disabled={!setup.data?.connected || voice.status === "connecting"}
              aria-pressed={voice.status === "live"}
              aria-label={
                voice.status === "live" ? "Stop voice" : "Talk about email"
              }
              title={
                voice.status === "live" ? "Stop voice" : "Talk about email"
              }
              onClick={() => {
                if (voice.status === "live") voice.stop();
                else {
                  void voice.start();
                }
              }}
              className="inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-accent font-medium text-white shadow-lg shadow-accent/20 transition hover:-translate-y-0.5 hover:shadow-xl focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent disabled:translate-y-0 disabled:opacity-50 motion-reduce:transform-none"
            >
              <FontAwesomeIcon
                icon={faMicrophone}
                className={
                  voice.status === "live"
                    ? "animate-pulse motion-reduce:animate-none"
                    : ""
                }
              />
            </button>
            {voice.error && <FieldError>{voice.error}</FieldError>}
            {voice.status === "unavailable" && (
              <FieldError>
                Email voice controls are not enabled in this environment.
              </FieldError>
            )}
          </PaperPanel>
          <div
            className={`grid gap-5 ${selection ? "xl:grid-cols-[minmax(240px,0.85fr)_minmax(0,1.15fr)]" : ""}`}
          >
            <section className="min-w-0">
              <div className="mb-4 flex flex-wrap gap-2">
                {["Brian’s work", "Inbox"].map((f) => (
                  <Button
                    key={f}
                    size="sm"
                    variant={filter === f ? "accent" : "outline"}
                    onClick={() => {
                      setFilter(f);
                      setSelection(undefined);
                      clearSearch();
                    }}
                  >
                    {f}
                  </Button>
                ))}
              </div>
              <h3 className="mb-3 text-sm font-medium">
                Conversations · {conversations.data?.total ?? 0}
              </h3>
              {conversations.isLoading && (
                <p role="status">Loading activity…</p>
              )}
              {!activity.length && !conversations.isLoading && (
                <PaperPanel className="p-6">
                  <p>No activity yet.</p>
                  <p className="mt-2 text-sm text-annotation">
                    Ask Brian to draft an email to get started. Its work will
                    appear here.
                  </p>
                </PaperPanel>
              )}
              <div className="flex max-h-[70vh] flex-col gap-2 overflow-y-auto p-1">
                {activity.map((a) => (
                  <button
                    key={`${a.kind}-${a.uuid}`}
                    aria-pressed={
                      selection?.uuid === a.uuid && selection.kind === a.kind
                    }
                    onClick={() => {
                      setFilter(filter);
                      setSelection({ kind: a.kind, uuid: a.uuid });
                      clearSearch();
                    }}
                    className={`w-full rounded-xl border border-current/10 bg-paper p-4 text-left transition hover:bg-recess ${selection?.uuid === a.uuid && selection.kind === a.kind ? "ring-2 ring-accent" : ""}`}
                  >
                    <div className="flex flex-wrap justify-between gap-2 text-xs text-annotation">
                      <span className="flex items-center gap-2">
                        {a.newReply && (
                          <span
                            className="size-2 rounded-full bg-accent"
                            aria-label="New reply"
                          />
                        )}
                        {a.summary}
                      </span>
                      <span>
                        {a.newReply && a.status === "received"
                          ? "New message"
                          : statusLabel(a.status)}
                      </span>
                    </div>
                    <p className="mt-2 break-words font-medium text-ink">
                      {a.title}
                    </p>
                    <p className="mt-1 line-clamp-2 break-words text-sm text-annotation">
                      {a.preview}
                    </p>
                    <p className="mt-3 text-xs text-annotation">
                      {a.label} · {formatDateTime(a.date)}
                    </p>
                  </button>
                ))}
                {conversations.hasNextPage && (
                  <Button
                    variant="outline"
                    disabled={conversations.isFetchingNextPage}
                    onClick={() => void conversations.fetchNextPage()}
                  >
                    {conversations.isFetchingNextPage
                      ? "Loading…"
                      : "Load more"}
                  </Button>
                )}
              </div>
            </section>
            {selection && (
              <section
                className={`order-first min-w-0 rounded-xl p-5 xl:order-none ${requestedDraft ? "border border-rule bg-paper shadow-sm" : "bg-recess"}`}
              >
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-display text-xl">Conversation</h3>
                  <div className="flex items-center gap-2">
                    {draft && isEditableDraft(draft.status) && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={discard.isPending}
                        onClick={() => discard.mutate(draft.uuid)}
                        className="gap-2 text-ink-stopped"
                      >
                        <FontAwesomeIcon icon={faTrash} aria-hidden="true" />
                        {discard.isPending ? "Deleting…" : "Delete draft"}
                      </Button>
                    )}
                    {requestedDraft ? (
                      <button
                        type="button"
                        onClick={minimizeDraft}
                        aria-label="Minimize draft"
                        title="Minimize"
                        className="flex size-8 items-center justify-center rounded-full text-annotation hover:bg-paper focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                      >
                        <FontAwesomeIcon icon={faMinus} />
                      </button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={minimizeDraft}
                      >
                        Close
                      </Button>
                    )}
                  </div>
                </div>
                {discard.error && (
                  <FieldError>
                    {describeMutationError(discard.error, "delete the draft")}
                  </FieldError>
                )}
                {message?.association && <EmailAssociation message={message} />}
                {draftQuery.error && (
                  <FieldError>
                    {describeMutationError(draftQuery.error, "load the draft")}
                  </FieldError>
                )}
                {messageQuery.error && (
                  <FieldError>
                    {describeMutationError(
                      messageQuery.error,
                      "load the email",
                    )}
                  </FieldError>
                )}
                {(draftQuery.isLoading || messageQuery.isLoading) && (
                  <p>Loading selected email…</p>
                )}
                <EmailConversationContext
                  context={context.data}
                  selectedMessage={message}
                  drafts={relatedDrafts}
                  loading={context.isLoading}
                  error={context.error}
                  canApprove={!!setup.data?.connected}
                  selectedDraftUuid={draftUuid}
                  onSelectDraft={(uuid) => openEmail({ draft: uuid })}
                />
                {context.hasNextPage && (
                  <Button
                    variant="outline"
                    className="mt-3"
                    disabled={context.isFetchingNextPage}
                    onClick={() => void context.fetchNextPage()}
                  >
                    {context.isFetchingNextPage
                      ? "Loading…"
                      : "Load earlier emails"}
                  </Button>
                )}
              </section>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};
